/** AN ORGANISATION'S GITHUB CONNECTION (coding harness stage 3; Tom,
 *  2026-11-03: only org owners and admins connect, disconnect or choose
 *  repos; server: routes/github_routes.py).
 *
 *  - The SourcePilot GitHub App is installed by an owner or admin of the
 *    organisation (`githubInstallUrl`, a navigation: GitHub returns to
 *    /connect-repo?org=…&github=connected|not_connected|taken|uninstalling|uninstalled|requested).
 *  - The owner or admin then chooses which of the repos the installation
 *    was granted SourcePilot uses (`chooseGitHubRepo`); those are the
 *    organisation's connected repos (`GitHubRepo`, read through the
 *    generated client: `rflp-organisation-github-repos`).
 *  - Members link a project or a software part to a connected repo through
 *    the registry (the generated client's modify), never here.
 *
 *  These are custom routes (not the registry), so they are fetched here, as
 *  company-org.ts does. A refusal is thrown as an ApiError with the server's
 *  own words, for `toRefusal`. */

import { ApiError } from "./api-error";
import { API_BASE_URL } from "./hooks/use-neo-client";

export type GitHubStatus = {
  /** this SourcePilot has a GitHub App configured */
  app_configured: boolean;
  connected: boolean;
  /** the GitHub account the App is installed on (no ids are served) */
  account_login: string | null;
  account_type: string | null;
  connected_at: string | null;
  /** the reader is an owner or admin of the organisation */
  can_manage: boolean;
};

export type GrantedRepo = {
  github_id: string;
  /** owner/name, as GitHub names it (repo-supplied text: shown as text only) */
  name: string;
  private: boolean;
  default_branch: string;
  html_url: string;
  /** the connected GitHubRepo's id once chosen, else null */
  connected_repo_id: string | null;
};

export type ChosenRepo = {
  id: string;
  name: string;
  default_branch: string;
  private: boolean;
  html_url: string;
  connected: boolean;
};

/** How GitHub's return to /connect-repo went (`?github=`). */
export type GitHubReturn = "connected" | "not_connected" | "taken" | "uninstalling" | "uninstalled" | "requested";

export function githubReturnWords(r: string | null): string | null {
  switch (r) {
    case "connected":
      return "GitHub is connected. Choose the repositories SourcePilot may use.";
    case "not_connected":
      return "GitHub was not connected: the installation could not be confirmed for your GitHub account. Try again.";
    case "taken":
      return "That GitHub installation is already connected to another organisation.";
    case "uninstalling":
      return "That GitHub installation is being uninstalled by an organisation that just disconnected it. Try again in a few minutes.";
    case "uninstalled":
      return "That GitHub installation was uninstalled meanwhile, so it was not connected. Install the GitHub App again.";
    case "requested":
      return "Your GitHub organisation's owner has been asked to approve the installation. Connect again once they have.";
    default:
      return null;
  }
}

async function githubJson<T>(path: string, init?: RequestInit): Promise<T | null> {
  const res = await fetch(`${API_BASE_URL}${path}`, { credentials: "include", cache: "no-store", ...init });
  if (res.status === 404 && !init?.method) return null;
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }
  if (!res.ok) throw new ApiError(res.status, parsed, text.slice(0, 300));
  return parsed as T;
}

const jsonBody = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

const base = (orgId: string) => `/organisations/${encodeURIComponent(orgId)}/github`;

/** The session's organisation (a session acts as one organisation). */
export async function readActiveOrganisationId(): Promise<string | null> {
  const r = await githubJson<{ organisations: { id: string; active: boolean }[] }>("/auth/organisations");
  return r?.organisations?.find((o) => o.active)?.id ?? null;
}

/** The organisation's connection, for anyone who reads it; null when not (404). */
export async function readGitHubStatus(orgId: string): Promise<GitHubStatus | null> {
  return githubJson<GitHubStatus>(base(orgId));
}

/** Where an owner or admin installs the GitHub App (a navigation, not a fetch:
 *  the server keeps the install's state in this session and redirects). */
export function githubInstallUrl(orgId: string): string {
  return `${API_BASE_URL}${base(orgId)}/install-url`;
}

/** The repos the installation was granted, live from GitHub (owners and admins). */
export async function readGrantedRepos(orgId: string): Promise<GrantedRepo[]> {
  const r = await githubJson<{ repos: GrantedRepo[] }>(`${base(orgId)}/granted-repos`);
  return r?.repos ?? [];
}

/** Choose a granted repo: it becomes one of the organisation's connected repos. */
export async function chooseGitHubRepo(orgId: string, githubId: string): Promise<ChosenRepo> {
  return (await githubJson<ChosenRepo>(`${base(orgId)}/repos`, jsonBody("POST", { github_id: githubId })))!;
}

/** What a disconnect did. With `uninstall`, `uninstalled: false` means GitHub
 *  did not confirm it: the App is still installed there, and
 *  `finish_uninstall_url` is the App's page on GitHub where it is finished. */
export type DisconnectResult = {
  disconnected: boolean;
  uninstalled: boolean;
  repos_disconnected: number;
  finish_uninstall_url?: string | null;
};

/** Disconnect GitHub: the repos are marked disconnected (links to them stay);
 *  `uninstall` also uninstalls the App on GitHub. */
export async function disconnectGitHub(orgId: string, uninstall: boolean): Promise<DisconnectResult> {
  return (await githubJson<DisconnectResult>(`${base(orgId)}/disconnect`, jsonBody("POST", { uninstall })))!;
}
